import React from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { getRoleDisplayName } from '../../utils/roleUtils';

// ✅ MODERNIZED: Glassmorphic Employee Search & Filter Bar
export const EmployeesSearchFilter = ({
  searchTerm,
  setSearchTerm,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
  availableRoles = []
}) => {
  // Board #956: offer the roles people on this team actually hold (Employees.js passes them),
  // not a fixed five that included office_staff — a role no row can have — and left out
  // apprentice, dispatcher and the rest. The current selection stays listed so a filter in
  // effect is never invisible.
  const roleOptions = [...new Set([...availableRoles, ...(roleFilter && roleFilter !== 'all' ? [roleFilter] : [])])]
    .sort((a, b) => getRoleDisplayName(a).localeCompare(getRoleDisplayName(b)));

  return (
    <div className="bg-gradient-to-br from-white/80 to-indigo-50/40 dark:from-slate-800/80 dark:to-slate-900/40 backdrop-blur-xl rounded-3xl border border-indigo-200/50 dark:border-indigo-900/50 shadow-2xl shadow-indigo-500/5 p-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Bar */}
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search employees by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/60 dark:bg-slate-700/60 border border-indigo-200/50 dark:border-indigo-900/50 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 font-medium"
            />
          </div>
        </div>

        {/* Role Filter */}
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-3 bg-white/60 dark:bg-slate-700/60 border border-indigo-200/50 dark:border-indigo-900/50 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 dark:text-white font-medium"
          >
            <option value="all">All Roles</option>
            {roleOptions.filter((r) => r !== 'no_login').map((role) => (
              <option key={role} value={role}>{getRoleDisplayName(role)}</option>
            ))}
            {/* Board #5205: the Role column prints "No login" for an employee with no user
                account (EmployeesTable.js). That is account state, not a role, so it is its own
                option rather than a fake enum value. */}
            <option value="no_login">No login</option>
          </select>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-white/60 dark:bg-slate-700/60 border border-indigo-200/50 dark:border-indigo-900/50 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 dark:text-white font-medium"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending Invite</option>
            {/* The Schedulable card sets this; with no option the box read "All Status" while
                the table was filtered (board #4285). on_leave/terminated are statuses the Edit
                form can set (board #4230). */}
            <option value="schedulable">Schedulable</option>
            <option value="on_leave">On Leave</option>
            {/* #6154: time off covering today, separate from the HR status "On Leave". */}
            <option value="off_today">Off today</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>
      </div>
    </div>
  );
};
